import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

interface VideoLightboxProps {
    videoId: string
    className?: string
    children: ReactNode
}

export const VideoLightbox: FC<VideoLightboxProps> = ({ videoId, className, children }): ReactNode => {
    const [open, setOpen] = useState(false)
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = previousOverflow
            window.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    return (
        <>
            <button
                type='button'
                onClick={() => setOpen(true)}
                aria-label='Play video'
                className={className}
            >
                {children}
            </button>

            {open && mounted && createPortal(
                <div
                    role='dialog'
                    aria-modal='true'
                    aria-label='Video player'
                    onClick={() => setOpen(false)}
                    className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm animate-in fade-in duration-200'
                >
                    <div
                        onClick={(event) => event.stopPropagation()}
                        className='relative w-full max-w-4xl'
                    >
                        <button
                            type='button'
                            onClick={() => setOpen(false)}
                            aria-label='Close video'
                            className='absolute -top-12 right-0 flex size-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white'
                        >
                            <X className='size-5' />
                        </button>
                        <div className='relative aspect-video w-full overflow-hidden rounded-2xl bg-black shadow-2xl'>
                            <iframe
                                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                                title='Video player'
                                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                                allowFullScreen
                                className='absolute inset-0 size-full border-0'
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    )
}
